import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthStore } from './auth.store';

interface AuthGuardOptions {
  requiredRoles?: string[];
  redirectTo?: string;
}

export function authGuard(options: AuthGuardOptions = {}): CanActivateFn {
  return (route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    const isAuthenticated = authStore.isAuthenticated();
    const user = authStore.user(); 

    if (!isAuthenticated) {
      const currentUrl = state.url;
      router.navigate([options.redirectTo || '/authentication/login'], {
        queryParams: { redirecturl: currentUrl },
      });
      return false;
    }

    if (options.requiredRoles) {
      const hasRequiredRole = options.requiredRoles.some(role =>
        user?.roles?.includes(role)
      );

      if (!hasRequiredRole) {
        router.navigate([options.redirectTo || '/authentication/login'], {
          queryParams: { redirecturl: state.url },
        });
        return false;
      }
    }

    return true;
  };
}

// LossRunProcessor may only reach the policy grid, a policy's pages, and their profile
const lossRunProcessorAllowedPaths = [/^\/policies(\?.*)?$/, /^\/policies\/(lossruns|documents)\/[^/?]+(\?.*)?$/, /^\/profile(\?.*)?$/];

export const lossRunProcessorGuard: CanActivateChildFn = (route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (!authStore.isLossRunProcessor()) {
    return true;
  }

  if (lossRunProcessorAllowedPaths.some((path) => path.test(state.url))) {
    return true;
  }

  // summary/pricing/etc. links into a policy land on its Loss Runs tab instead
  const policyMatch = state.url.match(/^\/policies\/[^/]+\/([^/?]+)/);
  if (policyMatch) {
    return router.createUrlTree(['/policies', 'lossruns', policyMatch[1]]);
  }

  return router.createUrlTree(['/policies']);
};